/* eslint-disable */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';

import { RegularButton, CommonCurrencyInput, CommonInput } from 'pg-ui-library';

import { BenefitSettingsType, CommuterSettings, IBenefit } from 'src/interfaces';

import * as Yup from 'yup';

import {
  CommuterSettingsFormContainer,
  DisabledInputContainer,
  FormItem,
  InputContainer,
  SubmitContainer,
} from './styles';
import { DateTime } from 'luxon';
import { useTypedSelector } from 'src/hooks/useTypedSelector';

export const CommuterSettingsForm: React.FC<{
  benefit: IBenefit;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (benefit: IBenefit) => void;
  onRequestBack?: () => void;
}> = ({ benefit, onSubmit, onRequestBack }) => {
  const { data, loading: personalInfoLoading } = useTypedSelector(state => state.personalInfo);
  const { enrollmentType } = useTypedSelector(state => state.benefits.data);
  const { t } = useTranslation();

  const getSchemaConfig = () => {
    if (!benefit.settings?.data) {
      return Yup.object({
        amount: Yup.number().min(0).required(),
      });
    }
    const valueLimit = (benefit.settings?.data as CommuterSettings).limitTheEmployeeCanPay;
    const minimumLimit = (benefit.settings?.data as CommuterSettings).minimumLimitTheEmployeeCanPay;
    if (valueLimit && minimumLimit) {
      return Yup.object({
        amount: Yup.number().min(minimumLimit).max(valueLimit).required(),
      });
    }
    if (valueLimit) {
      return Yup.object({
        amount: Yup.number().min(0).max(valueLimit).required(),
      });
    }
    return Yup.object({
      amount: Yup.number().min(0).required(),
    });
  };

  const schema = getSchemaConfig();

  const calculateRemainingPriceForSpendingAccounts = (biweekly: number) => {
    if (enrollmentType === 'open_enrollment') return biweekly;

    if (benefit.type === 'DCA' || benefit.type === 'FSA' || benefit.type === 'HSA') {
      const internalEmployee: boolean = data.personalInfo?.internalEmployee || false;
      if (data.personalInfo?.hireDate) {
        const luxonDate = DateTime.fromJSDate(new Date(data.personalInfo?.hireDate));

        const newDate = luxonDate.plus({ days: internalEmployee ? 30 : 60 });
        const effectStart = newDate.set({ day: 1 }).plus({ months: 1 });

        const remaingMonths = 12 - effectStart.month + 1;

        return (biweekly * 24) / (remaingMonths * 2);
      }
    }
    return biweekly;
  };

  const convertToBiWeeklyPrice = (price: number, { timeLimitThatEmployeePaymentApplies }: CommuterSettings) => {
    if (timeLimitThatEmployeePaymentApplies) {
      if (timeLimitThatEmployeePaymentApplies === 'Monthly') {
        return price / 2;
      }
      if (timeLimitThatEmployeePaymentApplies === 'Annual') {
        return price / 24;
      }
      if (
        timeLimitThatEmployeePaymentApplies === 'Bi-Weekly' ||
        timeLimitThatEmployeePaymentApplies === 'Semi Monthly'
      ) {
        return price;
      }
    }
    return price;
  };
  const initialValue = {
    amount: 0,
  };
  const initialIsValid = schema.isValidSync(initialValue);
  const formik = useFormik<{ amount: number }>({
    initialValues: initialValue,
    isInitialValid: initialIsValid,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: values =>
      onSubmit({
        ...benefit,
        price: calculateRemainingPriceForSpendingAccounts(
          convertToBiWeeklyPrice(Number(values.amount), benefit.settings?.data as CommuterSettings)
        ),
        settings: {
          type: (benefit.settings?.type as unknown as BenefitSettingsType.COMMUTER) || BenefitSettingsType.ACCOUNT,
          data: {
            ...(benefit.settings?.data as CommuterSettings),
            employeeChosenAmountToPay: Number(values.amount),
          },
        },
      }),
  });

  const getCommonCurrencyLabel = () => {
    if (benefit.type === 'DCA') {
      return 'Enter your annual goal amount for your Dependent Care Spending Account Elections';
    }
    if (benefit.type === 'FSA') {
      return 'Enter your annual goal amount for your Health Care Spending Account Elections';
    }
    if (benefit.type === 'HSA') {
      return 'Enter your annual goal amount for your Health Savings Account Elections';
    }
    return getLabelBasedOnPaymentApplies(benefit.settings?.data as CommuterSettings);
  };

  const getLabelBasedOnPaymentApplies = (commuterSettings: CommuterSettings) => {
    const { timeLimitThatEmployeePaymentApplies } = commuterSettings;

    if (timeLimitThatEmployeePaymentApplies) {
      if (timeLimitThatEmployeePaymentApplies === 'Monthly') {
        return 'Enter Monthly Price';
      }
      if (timeLimitThatEmployeePaymentApplies === 'Annual') {
        return 'Enter Annual Price';
      }
      if (timeLimitThatEmployeePaymentApplies === 'Semi Monthly') {
        return 'Enter Bi-Weekly Price';
      }
    }
    return 'Enter Amount';
  };

  const showPaymentPeriod = () => {
    if (benefit.type === 'DCA' || benefit.type === 'FSA' || benefit.type === 'HSA') {
      return false;
    }
    return true;
  };

  return (
    <CommuterSettingsFormContainer>
      <FormItem>
        <InputContainer>
          <CommonCurrencyInput
            id="amount"
            label={getCommonCurrencyLabel()}
            name="amount"
            placeholder="Please enter a number"
            prefix="$"
            decimalsLimit={2}
            allowNegativeValue={false}
            value={formik.values.amount}
            onValueChange={(val: any) => formik.setFieldValue('amount', val)}
          />
          <DisabledInputContainer>
            {benefit?.settings?.data &&
            'minimumLimitTheEmployeeCanPay' in benefit.settings.data &&
            benefit.settings.data.minimumLimitTheEmployeeCanPay != null &&
            benefit.settings.data.minimumLimitTheEmployeeCanPay > 0 ? (
              <CommonCurrencyInput
                label="Min Limit"
                prefix="$"
                decimalsLimit={2}
                allowNegativeValue={false}
                disabled
                value={benefit.settings.data.minimumLimitTheEmployeeCanPay ?? ''}
              />
            ) : (
              ''
            )}
            <CommonCurrencyInput
              label="Max Limit"
              prefix="$"
              decimalsLimit={2}
              allowNegativeValue={false}
              disabled
              value={
                benefit?.settings?.data
                  ? 'limitTheEmployeeCanPay' in benefit?.settings?.data
                    ? benefit.settings?.data?.limitTheEmployeeCanPay
                    : ''
                  : ''
              }
            />
            {showPaymentPeriod() ? (
              <CommonInput
                label="Payment Period"
                value={
                  benefit?.settings?.data
                    ? 'timeLimitThatEmployeePaymentApplies' in benefit?.settings?.data
                      ? benefit.settings?.data?.timeLimitThatEmployeePaymentApplies
                      : ''
                    : ''
                }
                disabled
              />
            ) : (
              <></>
            )}
          </DisabledInputContainer>
        </InputContainer>
      </FormItem>
      <SubmitContainer>
        {onRequestBack && <RegularButton onClick={onRequestBack}>{t('global.button.back')}</RegularButton>}
        <RegularButton
          disabled={!formik.isValid}
          style={{ margin: '0 3px 0 0' }}
          type="submit"
          onClick={() => formik.handleSubmit()}
        >
          {t('global.form.submit')}
        </RegularButton>
      </SubmitContainer>
    </CommuterSettingsFormContainer>
  );
};
